// Notification categories — eight at month 12, per Notifications doc §2.
// Used in NotificationPreference (Data Model §18) and to group the settings UI.

export const NOTIFICATION_CATEGORIES = [
  'account',
  'verification',
  'exchange',
  'review',
  'recap',
  'dispute',
  'subscription',
  'event',
  'event_interest',
  // Wave B return-hooks: planning-layer match notifications.
  'tour_route',
  'open_week',
  // Wave C return-hooks: Network Notes digest cadences.
  'network',
  'founder_letter',
  'marketing',
  // T2: trust + safety. Admin-facing receipts when a user files a report;
  // reporter-facing receipts when an admin resolves one.
  'report',
  // T5: artist→host attribution (North Star §7). Recipient is always the
  // referring artist — "someone joined through your link", "your referral
  // completed their first exchange — payable."
  'referral',
] as const;

export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export const NOTIFICATION_CHANNELS = ['in_app', 'email', 'web_push'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

// Default-enabled matrix per Notifications doc §2.
// `account.email` is always-on at the platform level (cannot be disabled).
// `founder_letter` is opt-out (default false). `marketing` is opt-in only.
export type NotificationDefault = {
  category: NotificationCategory;
  channel: NotificationChannel;
  enabled: boolean;
  /** True when the user cannot disable this combination via the settings UI. */
  required: boolean;
};

export const NOTIFICATION_DEFAULTS: readonly NotificationDefault[] = [
  // account
  { category: 'account', channel: 'in_app', enabled: true, required: false },
  { category: 'account', channel: 'email', enabled: true, required: true },
  { category: 'account', channel: 'web_push', enabled: true, required: false },
  // verification
  { category: 'verification', channel: 'in_app', enabled: true, required: false },
  { category: 'verification', channel: 'email', enabled: true, required: false },
  { category: 'verification', channel: 'web_push', enabled: true, required: false },
  // exchange
  { category: 'exchange', channel: 'in_app', enabled: true, required: false },
  { category: 'exchange', channel: 'email', enabled: true, required: false },
  { category: 'exchange', channel: 'web_push', enabled: true, required: false },
  // review
  { category: 'review', channel: 'in_app', enabled: true, required: false },
  { category: 'review', channel: 'email', enabled: true, required: false },
  { category: 'review', channel: 'web_push', enabled: true, required: false },
  // recap — Wave A return-hooks: published artifacts of completed exchanges (default on)
  { category: 'recap', channel: 'in_app', enabled: true, required: false },
  { category: 'recap', channel: 'email', enabled: true, required: false },
  { category: 'recap', channel: 'web_push', enabled: true, required: false },
  // dispute
  { category: 'dispute', channel: 'in_app', enabled: true, required: false },
  { category: 'dispute', channel: 'email', enabled: true, required: false },
  { category: 'dispute', channel: 'web_push', enabled: true, required: false },
  // subscription
  { category: 'subscription', channel: 'in_app', enabled: true, required: false },
  { category: 'subscription', channel: 'email', enabled: true, required: false },
  { category: 'subscription', channel: 'web_push', enabled: true, required: false },
  // event — host-posted gigs (default on)
  { category: 'event', channel: 'in_app', enabled: true, required: false },
  { category: 'event', channel: 'email', enabled: true, required: false },
  { category: 'event', channel: 'web_push', enabled: true, required: false },
  // event_interest — artist↔host interest decisions (default on)
  { category: 'event_interest', channel: 'in_app', enabled: true, required: false },
  { category: 'event_interest', channel: 'email', enabled: true, required: false },
  { category: 'event_interest', channel: 'web_push', enabled: true, required: false },
  // tour_route — Wave B return-hooks: a saved route matched something new (default on)
  { category: 'tour_route', channel: 'in_app', enabled: true, required: false },
  { category: 'tour_route', channel: 'email', enabled: true, required: false },
  { category: 'tour_route', channel: 'web_push', enabled: true, required: false },
  // open_week — Wave B return-hooks: a venue posted an open week matching a saved route (default on)
  { category: 'open_week', channel: 'in_app', enabled: true, required: false },
  { category: 'open_week', channel: 'email', enabled: true, required: false },
  { category: 'open_week', channel: 'web_push', enabled: true, required: false },
  // network — Wave C return-hooks: Network Notes digest (cadence cumulative across users.digest_*_enabled)
  { category: 'network', channel: 'in_app', enabled: true, required: false },
  { category: 'network', channel: 'email', enabled: true, required: false },
  { category: 'network', channel: 'web_push', enabled: false, required: false },
  // founder_letter — opt-out by default
  { category: 'founder_letter', channel: 'in_app', enabled: false, required: false },
  { category: 'founder_letter', channel: 'email', enabled: false, required: false },
  { category: 'founder_letter', channel: 'web_push', enabled: false, required: false },
  // marketing — opt-in only (default disabled)
  { category: 'marketing', channel: 'in_app', enabled: false, required: false },
  { category: 'marketing', channel: 'email', enabled: false, required: false },
  { category: 'marketing', channel: 'web_push', enabled: false, required: false },
  // report — trust signals. Admins want to see these (in_app + email on);
  // push stays off to avoid pager-style buzz on low-severity flags.
  { category: 'report', channel: 'in_app', enabled: true, required: false },
  { category: 'report', channel: 'email', enabled: true, required: false },
  { category: 'report', channel: 'web_push', enabled: false, required: false },
  // referral — sparse North Star §7 attribution signals. In-app + email on so
  // the referring artist actually sees the conversion; push off because the
  // events are rare enough that a system pager is overkill.
  { category: 'referral', channel: 'in_app', enabled: true, required: false },
  { category: 'referral', channel: 'email', enabled: true, required: false },
  { category: 'referral', channel: 'web_push', enabled: false, required: false },
] as const;
